import { useState, useEffect, useCallback, useMemo } from 'react'
import { Box, Button, Heading, HStack, SimpleGrid, IconButton, Text, Input, Stack } from '@chakra-ui/react'
import { NativeSelectRoot, NativeSelectField } from '@chakra-ui/react'
import { Field } from '@chakra-ui/react'
import { Switch } from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import { Plus, LayoutGrid, Table, ChevronLeft, ChevronRight, Search, ChevronDown } from 'lucide-react'
import { useDebounce } from 'use-debounce'
import { useOwnersPaginated, useDeleteOwner } from '../hooks/useOwners'
import { useProperties } from '@/features/properties/hooks/useProperties'
import { useAuth } from '@/features/auth/hooks/useAuth'
import { OwnerCard } from '../components/OwnerCard'
import { OwnerTable } from '../components/OwnerTable'
import { OwnerDetailDrawer } from '../components/OwnerDetailDrawer'
import { OwnerCreateModal } from '../components/OwnerCreateModal'
import { LoadingState } from '@/shared/components/LoadingState'
import { ErrorState } from '@/shared/components/ErrorState'
import { EmptyState } from '@/shared/components/EmptyState'
import type { OwnerSortField } from '../types'

type ViewMode = 'cards' | 'table'
type SortDirection = 'asc' | 'desc'

const ITEMS_PER_PAGE = 20

export function OwnersPage() {
  const { t } = useTranslation()
  const { data: properties = [] } = useProperties()
  const { data: auth } = useAuth()
  const deleteOwner = useDeleteOwner()
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [viewMode, setViewMode] = useState<ViewMode>('table')
  const [selectedOwnerId, setSelectedOwnerId] = useState<string | null>(null)
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [sortField, setSortField] = useState<OwnerSortField>('last_name')
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc')
  const [filterResidenceId, setFilterResidenceId] = useState<string>('')
  const [filterUnitType, setFilterUnitType] = useState<string>('')
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [showCurrentOnly, setShowCurrentOnly] = useState<boolean>(true)

  // Debounce search term to avoid too many queries
  const [debouncedSearchTerm] = useDebounce(searchTerm, 500)

  // Fetch owners with server-side pagination, filtering, and sorting
  const {
    data: paginatedData,
    isLoading,
    error,
    refetch,
  } = useOwnersPaginated({
    residenceId: filterResidenceId || undefined,
    unitType: filterUnitType || undefined,
    searchTerm: debouncedSearchTerm || undefined,
    includeFormer: !showCurrentOnly, // Inverse: si showCurrentOnly=true, includeFormer=false
    page: currentPage,
    pageSize: ITEMS_PER_PAGE,
    sortField,
    sortDirection,
  })

  // Extract data from paginated response
  const owners = paginatedData?.data || []
  const totalPages = paginatedData?.totalPages || 0
  const totalCount = paginatedData?.count || 0

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1)
  }, [filterResidenceId, filterUnitType, debouncedSearchTerm, showCurrentOnly, sortField, sortDirection])

  // Check delete permissions
  // Only ADMIN can delete owners
  const canDelete = useMemo(() => {
    if (!auth) return false
    return auth.isAdmin
  }, [auth])

  const handleDelete = async (id: string) => {
    if (!window.confirm(t('owners.delete_confirm'))) return

    setDeletingId(id)
    try {
      await deleteOwner.mutateAsync(id)
      // Close drawer if the deleted owner was selected
      if (selectedOwnerId === id) {
        setIsDrawerOpen(false)
        setSelectedOwnerId(null)
      }
    } catch (error) {
      console.error('Error deleting owner:', error)
      alert(t('common.error'))
    } finally {
      setDeletingId(null)
    }
  }

  const handleOwnerClick = (id: string) => {
    setSelectedOwnerId(id)
    setIsDrawerOpen(true)
  }

  const handleDrawerClose = () => {
    setIsDrawerOpen(false)
    setSelectedOwnerId(null)
  }

  // Navigation functions
  const getCurrentIndex = useCallback(() => {
    if (!selectedOwnerId || !owners) return -1
    return owners.findIndex((o) => o.id === selectedOwnerId)
  }, [selectedOwnerId, owners])

  const handleNavigatePrevious = useCallback(() => {
    if (!owners || owners.length === 0) return
    const currentIndex = getCurrentIndex()
    if (currentIndex > 0) {
      const previousOwner = owners[currentIndex - 1]
      if (previousOwner) {
        setSelectedOwnerId(previousOwner.id)
      }
    }
  }, [owners, getCurrentIndex])

  const handleNavigateNext = useCallback(() => {
    if (!owners || owners.length === 0) return
    const currentIndex = getCurrentIndex()
    if (currentIndex >= 0 && currentIndex < owners.length - 1) {
      const nextOwner = owners[currentIndex + 1]
      if (nextOwner) {
        setSelectedOwnerId(nextOwner.id)
      }
    }
  }, [owners, getCurrentIndex])

  const hasPrevious = getCurrentIndex() > 0
  const hasNext = getCurrentIndex() >= 0 && getCurrentIndex() < (owners?.length ?? 0) - 1

  // Sort handler
  const handleSort = (field: OwnerSortField) => {
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      // New field, default to ascending
      setSortField(field)
      setSortDirection('asc')
    }
  }

  // Keyboard navigation
  useEffect(() => {
    if (!isDrawerOpen) return

    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore if user is typing in an input field
      const target = e.target as HTMLElement
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        return
      }

      if (e.key === 'ArrowUp') {
        e.preventDefault()
        handleNavigatePrevious()
      } else if (e.key === 'ArrowDown') {
        e.preventDefault()
        handleNavigateNext()
      } else if (e.key === 'Escape') {
        handleDrawerClose()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isDrawerOpen, handleNavigatePrevious, handleNavigateNext])

  if (isLoading) {
    return <LoadingState message={t('common.loading')} />
  }

  if (error) {
    return <ErrorState message={t('common.error')} onRetry={() => refetch()} />
  }

  // Check if filters are active
  const hasActiveFilters = !!(
    filterResidenceId ||
    filterUnitType ||
    debouncedSearchTerm ||
    !showCurrentOnly
  )

  if (!owners || owners.length === 0) {
    // If no filters are active, show "create first owner" empty state
    if (!hasActiveFilters) {
      return (
        <>
          <EmptyState
            title={t('owners.no_owners')}
            description={t('owners.create_first')}
            action={{
              label: t('owners.create'),
              onClick: () => setIsCreateModalOpen(true),
            }}
          />
          <OwnerCreateModal
            isOpen={isCreateModalOpen}
            onClose={() => setIsCreateModalOpen(false)}
            onSuccess={(ownerId) => {
              setSelectedOwnerId(ownerId)
              setIsDrawerOpen(true)
            }}
          />
        </>
      )
    }
    // If filters are active but no results, show "no results" message inline
    // This will be handled in the main render below
  }

  return (
    <Box>
      <HStack justify="space-between" mb={4}>
        <Heading size="lg">{t('owners.title')}</Heading>
        <HStack gap={2}>
          <HStack gap={1} borderWidth="1px" borderRadius="md" p={1}>
            <IconButton
              aria-label={t('owners.view_mode.cards')}
              title={t('owners.view_mode.cards')}
              size="sm"
              variant={viewMode === 'cards' ? 'solid' : 'ghost'}
              onClick={() => setViewMode('cards')}
            >
              <LayoutGrid size={18} />
            </IconButton>
            <IconButton
              aria-label={t('owners.view_mode.table')}
              title={t('owners.view_mode.table')}
              size="sm"
              variant={viewMode === 'table' ? 'solid' : 'ghost'}
              onClick={() => setViewMode('table')}
            >
              <Table size={18} />
            </IconButton>
          </HStack>
          <Button colorPalette="brand" onClick={() => setIsCreateModalOpen(true)}>
            <HStack gap={2}>
              <Plus size={20} />
              <span>{t('owners.create')}</span>
            </HStack>
          </Button>
        </HStack>
      </HStack>

      {/* Filters */}
      <Stack direction={{ base: 'column', md: 'row' }} gap={4} mb={6} flexWrap="wrap">
        <Field.Root w={{ base: 'full', md: '280px' }}>
          <NativeSelectRoot position="relative">
            <NativeSelectField
              value={filterResidenceId}
              onChange={(e) => setFilterResidenceId(e.target.value)}
              paddingRight="10"
            >
              <option value="">Toutes les résidences</option>
              {properties.map((property) => (
                <option key={property.id} value={property.id}>
                  {property.residence_key
                    ? `${property.residence_key} - ${property.name}`
                    : property.name}
                </option>
              ))}
            </NativeSelectField>
            <Box
              position="absolute"
              right="3"
              top="50%"
              transform="translateY(-50%)"
              pointerEvents="none"
              color="gray.500"
            >
              <ChevronDown size={16} />
            </Box>
          </NativeSelectRoot>
        </Field.Root>

        <Field.Root w={{ base: 'full', md: '280px' }}>
          <NativeSelectRoot position="relative">
            <NativeSelectField
              value={filterUnitType}
              onChange={(e) => setFilterUnitType(e.target.value)}
              paddingRight="10"
            >
              <option value="">Tous les types de lots</option>
              <option value="RESIDENTIAL">Résidentiel</option>
              <option value="PARKING">Parking</option>
              <option value="CELLAR">Cave</option>
              <option value="COMMERCIAL">Commercial</option>
            </NativeSelectField>
            <Box
              position="absolute"
              right="3"
              top="50%"
              transform="translateY(-50%)"
              pointerEvents="none"
              color="gray.500"
            >
              <ChevronDown size={16} />
            </Box>
          </NativeSelectRoot>
        </Field.Root>

        <Field.Root w={{ base: 'full', md: '310px' }}>
          <Box position="relative" w="full">
            <Search size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#718096', pointerEvents: 'none' }} />
            <Input
              placeholder="Rechercher par nom, prénom ou email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              pl="40px"
              w="full"
            />
          </Box>
        </Field.Root>

        <Box w={{ base: 'full', md: 'auto' }}>
          <HStack
            gap={3}
            cursor="pointer"
            onClick={() => setShowCurrentOnly(!showCurrentOnly)}
            h="40px"
          >
            <Switch.Root
              checked={showCurrentOnly}
              colorPalette="brand"
            >
              <Switch.Control>
                <Switch.Thumb />
              </Switch.Control>
            </Switch.Root>
            <Text fontSize="sm" fontWeight="medium" color="gray.700" whiteSpace="nowrap">
              Copropriétaires en cours uniquement
            </Text>
          </HStack>
        </Box>
      </Stack>

      {owners.length === 0 ? (
        <Box
          p={12}
          textAlign="center"
          borderWidth="1px"
          borderRadius="lg"
          borderStyle="dashed"
          borderColor="gray.300"
        >
          <Text fontSize="lg" fontWeight="medium" color="gray.600" mb={2}>
            Aucun résultat trouvé
          </Text>
          <Text fontSize="sm" color="gray.500">
            Essayez de modifier vos critères de recherche ou vos filtres
          </Text>
        </Box>
      ) : viewMode === 'cards' ? (
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} gap={6}>
          {owners.map((owner) => (
            <OwnerCard
              key={owner.id}
              owner={owner}
              isSelected={selectedOwnerId === owner.id}
              onClick={handleOwnerClick}
              onDelete={
                deletingId === owner.id || !canDelete ? undefined : handleDelete
              }
            />
          ))}
        </SimpleGrid>
      ) : (
        <OwnerTable
          owners={owners}
          selectedOwnerId={selectedOwnerId}
          onOwnerClick={handleOwnerClick}
          onDelete={deletingId ? undefined : handleDelete}
          canDelete={() => canDelete}
          sortField={sortField}
          sortDirection={sortDirection}
          onSort={handleSort}
        />
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <HStack justify="space-between" mt={6} pt={6} borderTopWidth="1px">
          <Text fontSize="sm" color="gray.600">
            {t('common.showing')} {(currentPage - 1) * ITEMS_PER_PAGE + 1}-
            {Math.min(currentPage * ITEMS_PER_PAGE, totalCount)} {t('common.of')} {totalCount}
          </Text>
          <HStack gap={2}>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              <ChevronLeft size={16} />
              <span>{t('common.previous')}</span>
            </Button>
            <Text fontSize="sm" fontWeight="medium">
              {currentPage} / {totalPages}
            </Text>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              <span>{t('common.next')}</span>
              <ChevronRight size={16} />
            </Button>
          </HStack>
        </HStack>
      )}

      <OwnerCreateModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={(ownerId) => {
          setSelectedOwnerId(ownerId)
          setIsDrawerOpen(true)
        }}
      />

      <OwnerDetailDrawer
        ownerId={selectedOwnerId}
        isOpen={isDrawerOpen}
        onClose={handleDrawerClose}
        onNavigatePrevious={handleNavigatePrevious}
        onNavigateNext={handleNavigateNext}
        hasPrevious={hasPrevious}
        hasNext={hasNext}
      />
    </Box>
  )
}
